/**
 * Panel — conteneur générique réutilisé 3× (gauche / centre / droite).
 *
 * @remarks
 * Composant agnostique au métier : reçoit des tabs + un activeTabId, rend
 * la barre de tabs et un slot pour le contenu. Wrap le contenu dans un
 * `PanelErrorBoundary` → un crash de feature n'affecte qu'un panneau.
 */

import { Suspense, type ReactNode } from 'react';
import type { TabConfig } from '@/app/registry';
import { PanelTabs } from './PanelTabs';
import { PanelErrorBoundary } from './PanelErrorBoundary';
import { LoadingState } from '@/shared/components/screen-state';

interface Props {
  /** Onglets à afficher. Si vide, le panneau ne rend pas de barre. */
  tabs?: readonly TabConfig[];
  /** ID du tab actif. Si null/undefined, pas de View rendue. */
  activeTabId?: string | null;
  /** Handler de clic sur un tab. */
  onTabClick?: (id: string) => void;
  /** Côté du panneau (utile pour le styling et la logique drag). */
  side: 'left' | 'center' | 'right';
  /** Contenu du panneau. Pour le centre, c'est juste un ReactNode. Pour les côtés, le composant `view` du tab actif est rendu en Suspense. */
  children?: ReactNode;
  /** Bandeau optionnel en tête de panneau (sous les tabs), au-dessus du contenu. */
  header?: ReactNode;
  /** Bandeau optionnel en pied de panneau. */
  footer?: ReactNode;
}

export function Panel({
  tabs = [],
  activeTabId = null,
  onTabClick,
  side,
  children,
  header,
  footer,
}: Props) {
  const activeTab = tabs.find((t) => t.id === activeTabId) ?? null;

  return (
    <section
      data-panel-side={side}
      className="bg-surface flex h-full flex-col"
      aria-label={`Panneau ${side}`}
      // Cible du lien d'évitement et du focus de navigation (SAAS-915) : sur
      // desktop, c'est le seul des 3 panneaux qui joue ce rôle. Tablet et
      // Mobile ont leur propre <main>, qui porte le même id.
      {...(side === 'center' && { id: 'main-content', tabIndex: -1 })}
    >
      {tabs.length > 0 && side !== 'center' && (
        <PanelTabs
          tabs={tabs}
          activeTabId={activeTabId}
          onTabClick={(id) => onTabClick?.(id)}
          side={side}
        />
      )}

      {header}

      {side === 'center' ? (
        /* Two-div structure for center panel:
         * outer = `flex-1` + `overflow-y-auto` → fills panel; scrollbar quand le
         *         contenu dépasse, ancrée au bord droit du panel.
         * inner = `h-full` → height définie (= outer height) pour que les views
         *         qui utilisent `h-full` (éditeurs Lexical / Excalidraw) résolvent
         *         `height: 100%` correctement. Le scroll de l'outer reste actif
         *         pour les vues plus longues que le viewport — il mesure
         *         `scrollHeight` (qui inclut l'overflow des descendants).
         *         Le centrage en mode "centered" reste piloté par la règle
         *         globale [data-content-layout='centered'] [data-panel-center-inner]
         *         dans globals.css (max-width + margins). */
        <div className="relative min-h-0 min-w-0 flex-1 overflow-y-auto">
          <div data-panel-center-inner className="h-full w-full">
            <PanelErrorBoundary panelId={`panel-${side}`}>
              <Suspense fallback={<PanelLoading />}>
                {children}
                {!children && activeTab && <ActiveView tab={activeTab} />}
                {!children && !activeTab && tabs.length > 0 && <EmptyTabs />}
              </Suspense>
            </PanelErrorBoundary>
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-auto">
          <PanelErrorBoundary panelId={`panel-${side}`}>
            <Suspense fallback={<PanelLoading />}>
              {children}
              {!children && activeTab && <ActiveView tab={activeTab} />}
              {!children && !activeTab && tabs.length > 0 && <EmptyTabs />}
            </Suspense>
          </PanelErrorBoundary>
        </div>
      )}

      {footer && <div className="border-border shrink-0 border-t">{footer}</div>}
    </section>
  );
}

function ActiveView({ tab }: { tab: TabConfig }) {
  const View = tab.view;
  return <View />;
}

function PanelLoading() {
  return <LoadingState label="Chargement du panneau…" />;
}

function EmptyTabs() {
  return (
    <div className="text-fg-muted flex h-full items-center justify-center px-4 text-center text-xs">
      Sélectionne un onglet
    </div>
  );
}
